import { boolOrIntCodec, timestampCodec } from '../../validation/codecs';
import {mixed, optional, required, t} from '../../validation/validator';
import { KitRecordSchema } from '../avatars/avatarSchemas';
import { CallResponseSchema } from '../common/callResponseSchema';
import { UserRecordSchema } from '../users/userSchemas';
import { UpdateTeamSchema } from './updateTeamSchema';

/****************************************************
 * Team Schemas START
 ****************************************************/

// Team Schema
const TeamConfigSchema = mixed([
    required({
        name: t.string,
        home_kit: t.number,
        away_kit: t.number,
    }),
    optional({
        is_global: boolOrIntCodec,
    })
]);

// io-ts codec for validating field is an unsigned integer and not a float
const uintCodec = t.refinement(t.number, (n) => n >= 0 && n % 1 === 0);

// Team Record Schema
export const TeamConfigRecordSchema = mixed([
    TeamConfigSchema,
    required({
        // Required fields
        id: uintCodec,
        created_at: timestampCodec,
        updated_at: t.union([timestampCodec, t.undefined, t.null])
    })
]);
export type TeamConfigRecord = t.TypeOf<typeof TeamConfigRecordSchema>;

export const TeamConfigResponseSchema = mixed([
    required({
        home_kit: KitRecordSchema,
        away_kit: KitRecordSchema
    }),
    TeamConfigRecordSchema,
]);

export type TeamConfigResponse = t.TypeOf<typeof TeamConfigResponseSchema>;

// Team Insert Schema
const TeamConfigInsertSchema = TeamConfigSchema;
export type TeamConfigInsert = t.TypeOf<typeof TeamConfigInsertSchema>;

// Team Update Schema
const UpdateTeamConfigRequestSchema = mixed([
    TeamConfigSchema,
    required({
        create: t.boolean
    }),
    optional({
        team_config_id: t.number,
        is_global: boolOrIntCodec
    })
]);
export type UpdateTeamConfigRequest = t.TypeOf<typeof UpdateTeamConfigRequestSchema>;

// Filter Options Type
export type TeamFilters = {
    has_club?: boolean;
    min_members?: number;
    has_group?: boolean;
    has_accounts?: 'with' | 'without' | 'all';
    has_business_account?: boolean;
};

// Search Teams Schema
export const SearchTeamsRequestSchema = mixed([
    required({
        search_query: t.string,
    }),
    optional({
        limit: t.union([t.number, t.undefined]),
        skip: t.union([t.number, t.undefined]),
        include_accounts: t.array(t.number),
        exclude_accounts: t.array(t.number),
        resolve_members: t.boolean,
        resolve_clubs: t.boolean,
        filters: t.partial({
            has_club: t.boolean,
            min_members: t.number,
            has_group: t.boolean,
            has_accounts: t.union([t.literal('with'), t.literal('without'), t.literal('all')]),
            has_business_account: t.boolean
        })
    })
]);
export type SearchTeamsRequest = t.TypeOf<typeof SearchTeamsRequestSchema>;

// Team Record Common Fields Schema
export const TeamRecordCommonFields = {
    doc_id: t.union([t.string, t.null]),
    name: t.union([t.string, t.null]),
    group: t.union([t.string, t.null]),
    license_provider_id: t.union([t.number, t.null]),
    club_id: t.union([t.number, t.null]),
};

// Team Record Schema
export const TeamRecordSchema = t.intersection([
    t.type({
        id: t.number,
        created_at: timestampCodec,
        updated_at: t.union([timestampCodec, t.null]),
    }),
    t.partial(TeamRecordCommonFields),
    t.partial({
        members: t.array(t.type({
            user_id: t.number,
            role: t.string
        })),
        member_records: t.array(UserRecordSchema)
    })
]);

export type TeamRecord = t.TypeOf<typeof TeamRecordSchema>;

// Team Record Insert Schema
export const TeamRecordInsertSchema = t.intersection([
    t.type({
        id: t.number,
        updated_at: t.union([timestampCodec, t.null]),
    }),
    t.partial(TeamRecordCommonFields)
])

export type TeamRecordInsert = t.TypeOf<typeof TeamRecordInsertSchema>;


/****************************************************
 * Team Schemas END
 ****************************************************/
export const TeamApis = {
    Teams: {
        UpdateTeam: {
            Name: "teams-updateTeam",
            Request: UpdateTeamSchema,
            Response: mixed([
                required({ 
                    data: t.union([
                        t.undefined,
                        t.partial({ team_id: t.number })
                    ])
                }),
                CallResponseSchema
            ])
        },
        UpdateTeamConfig: {
            Name: "teams-updateTeamConfig",
            Request: UpdateTeamConfigRequestSchema,
            Response: mixed([
                required({ data: TeamConfigRecordSchema }),
                CallResponseSchema
            ])
        },
        GetTeamConfigs: {
            Name: "teams-getTeamConfigs",
            Request: optional({
                account_id: t.number,
                user_id: t.number,
                user_doc_id: t.string,
                resolve_user_account: t.boolean,
                team_config_ids: t.array(t.number),
                search_query: t.string,
                limit: t.number,
                offset: t.number,
                global_only: t.boolean
            }),
            Response: mixed([
                required({ 
                    data: t.array(TeamConfigResponseSchema),
                    total_count: t.number
                }),
                CallResponseSchema
            ])
        },
        SearchTeams: {
            Name: "teams-searchTeams",
            Request: SearchTeamsRequestSchema,
            Response: mixed([
                required({ 
                    data: t.array(TeamRecordSchema),
                    total_count: t.number
                }), 
                CallResponseSchema
            ])
        }
    }
};
